import React, { createContext, useContext, useState, useEffect } from 'react';
import { format } from 'date-fns';
import supabase from '../lib/supabase';
import { useAuth } from './AuthContext';

const DataContext = createContext();

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [notes, setNotes] = useState([]);
  const [memberGoals, setMemberGoals] = useState([]);
  const [memberMiniGoals, setMemberMiniGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [offlineMode, setOfflineMode] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  // Setup offline mode detection
  useEffect(() => {
    const handleOnlineStatus = () => {
      setOfflineMode(!navigator.onLine);
    };

    window.addEventListener('online', handleOnlineStatus);
    window.addEventListener('offline', handleOnlineStatus);

    // Initial check
    setOfflineMode(!navigator.onLine);

    return () => {
      window.removeEventListener('online', handleOnlineStatus);
      window.removeEventListener('offline', handleOnlineStatus);
    };
  }, []);

  // Setup IndexedDB for offline data
  useEffect(() => {
    const setupIndexedDB = async () => {
      // Check if IndexedDB is supported
      if (!window.indexedDB) {
        console.log("Your browser doesn't support IndexedDB");
        return;
      }

      try {
        // Open a database
        const request = window.indexedDB.open("bpaOfflineDB", 3); // Increment version for new stores

        request.onerror = (event) => {
          console.error("IndexedDB error:", event.target.error);
        };

        request.onupgradeneeded = (event) => {
          const db = event.target.result;

          // Create object stores
          if (!db.objectStoreNames.contains("members")) {
            db.createObjectStore("members", { keyPath: "id" });
          }
          if (!db.objectStoreNames.contains("notes")) {
            db.createObjectStore("notes", { keyPath: "id" });
          }
          if (!db.objectStoreNames.contains("memberGoals")) {
            db.createObjectStore("memberGoals", { keyPath: "id" });
          }
          if (!db.objectStoreNames.contains("memberMiniGoals")) {
            db.createObjectStore("memberMiniGoals", { keyPath: "id" });
          }
          if (!db.objectStoreNames.contains("pendingActions")) {
            const pendingStore = db.createObjectStore("pendingActions", { keyPath: "id", autoIncrement: true });
            pendingStore.createIndex("type", "type", { unique: false });
          }
        };
      } catch (error) {
        console.error("Error setting up IndexedDB:", error);
      }
    };

    setupIndexedDB();
  }, []);

  // Fetch members, notes, goals, and mini-goals on initial load
  useEffect(() => {
    const fetchData = async () => {
      if (!user) {
        setMembers([]);
        setNotes([]);
        setMemberGoals([]);
        setMemberMiniGoals([]);
        setLoading(false);
        return;
      }
      setLoading(true);

      try {
        // First try to load from IndexedDB for instant display
        await loadFromIndexedDB();

        // Then fetch from API if online
        if (navigator.onLine) {
          // Fetch members
          const { data: membersData, error: membersError } = await supabase
            .from('members_bpa_5e7gf')
            .select('*')
            .order('name');

          if (membersError) {
            console.error('Error fetching members:', membersError);
            // If we already loaded from IndexedDB, don't fall back to mock data
            if (members.length === 0) setMembers([]);
          } else {
            setMembers(membersData || []);
            // Save to IndexedDB
            saveToIndexedDB("members", membersData || []);
          }

          // Fetch notes
          const { data: notesData, error: notesError } = await supabase
            .from('notes_bpa_5e7gf')
            .select('*')
            .order('timestamp', { ascending: false });

          if (notesError) {
            console.error('Error fetching notes:', notesError);
            // If we already loaded from IndexedDB, don't fall back to mock data
            if (notes.length === 0) setNotes([]);
          } else {
            setNotes(notesData || []);
            // Save to IndexedDB
            saveToIndexedDB("notes", notesData || []);
          }

          // Fetch member goals
          const { data: goalsData, error: goalsError } = await supabase
            .from('member_goals_bpa_5e7gf')
            .select('*')
            .order('created_at', { ascending: false });

          if (goalsError) {
            console.error('Error fetching member goals:', goalsError);
            // If we already loaded from IndexedDB, don't fall back to mock data
            if (memberGoals.length === 0) {
              setMemberGoals([]);
            }
          } else {
            setMemberGoals(goalsData || []);
            // Save to IndexedDB
            saveToIndexedDB("memberGoals", goalsData || []);
          }

          // Fetch member mini-goals
          const { data: miniGoalsData, error: miniGoalsError } = await supabase
            .from('member_mini_goals_bpa_5e7gf')
            .select('*')
            .order('created_at', { ascending: false });

          if (miniGoalsError) {
            console.error('Error fetching member mini-goals:', miniGoalsError);
            // If we already loaded from IndexedDB, don't fall back to mock data
            if (memberMiniGoals.length === 0) {
              setMemberMiniGoals([]);
            }
          } else {
            setMemberMiniGoals(miniGoalsData || []);
            // Save to IndexedDB
            saveToIndexedDB("memberMiniGoals", miniGoalsData || []);
          }

          // Try to sync any pending actions
          syncPendingActions();
        }
      } catch (err) {
        console.error('Error in fetchData:', err);
        // If we haven't loaded data from IndexedDB, use mock data
        if (members.length === 0) setMembers([]);
        if (notes.length === 0) setNotes([]);
        if (memberGoals.length === 0) {
          setMemberGoals([]);
        }
        if (memberMiniGoals.length === 0) {
          setMemberMiniGoals([]);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  // Load data from IndexedDB
  const loadFromIndexedDB = async () => {
    try {
      const db = await new Promise((resolve, reject) => {
        const request = window.indexedDB.open("bpaOfflineDB", 3);
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result);
      });

      // Load members
      const membersData = await new Promise((resolve, reject) => {
        const transaction = db.transaction("members", "readonly");
        const store = transaction.objectStore("members");
        const request = store.getAll();
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result);
      });

      if (membersData && membersData.length > 0) {
        setMembers(membersData);
      }

      // Load notes
      const notesData = await new Promise((resolve, reject) => {
        const transaction = db.transaction("notes", "readonly");
        const store = transaction.objectStore("notes");
        const request = store.getAll();
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result);
      });

      if (notesData && notesData.length > 0) {
        setNotes(notesData);
      }

      // Load member goals
      const goalsData = await new Promise((resolve, reject) => {
        const transaction = db.transaction("memberGoals", "readonly");
        const store = transaction.objectStore("memberGoals");
        const request = store.getAll();
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result);
      });

      if (goalsData && goalsData.length > 0) {
        setMemberGoals(goalsData);
      }

      // Load member mini-goals
      const miniGoalsData = await new Promise((resolve, reject) => {
        const transaction = db.transaction("memberMiniGoals", "readonly");
        const store = transaction.objectStore("memberMiniGoals");
        const request = store.getAll();
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result);
      });

      if (miniGoalsData && miniGoalsData.length > 0) {
        setMemberMiniGoals(miniGoalsData);
      }
    } catch (error) {
      console.error("Error loading from IndexedDB:", error);
    }
  };

  // Save data to IndexedDB
  const saveToIndexedDB = async (storeName, data) => {
    try {
      const db = await new Promise((resolve, reject) => {
        const request = window.indexedDB.open("bpaOfflineDB", 3);
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result);
      });

      const transaction = db.transaction(storeName, "readwrite");
      const store = transaction.objectStore(storeName);

      // Clear existing data
      await new Promise((resolve, reject) => {
        const clearRequest = store.clear();
        clearRequest.onerror = () => reject(clearRequest.error);
        clearRequest.onsuccess = () => resolve();
      });

      // Add all items
      for (const item of data) {
        await new Promise((resolve, reject) => {
          const addRequest = store.add(item);
          addRequest.onerror = () => reject(addRequest.error);
          addRequest.onsuccess = () => resolve();
        });
      }
    } catch (error) {
      console.error(`Error saving to IndexedDB (${storeName}):`, error);
    }
  };

  // Add pending action to IndexedDB
  const addPendingAction = async (type, data) => {
    try {
      const db = await new Promise((resolve, reject) => {
        const request = window.indexedDB.open("bpaOfflineDB", 3);
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result);
      });

      const transaction = db.transaction("pendingActions", "readwrite");
      const store = transaction.objectStore("pendingActions");

      const pendingAction = {
        type,
        data,
        timestamp: new Date().toISOString()
      };

      await new Promise((resolve, reject) => {
        const addRequest = store.add(pendingAction);
        addRequest.onerror = () => reject(addRequest.error);
        addRequest.onsuccess = () => resolve();
      });
    } catch (error) {
      console.error("Error adding pending action to IndexedDB:", error);
    }
  };

  // Sync pending actions when online
  const syncPendingActions = async () => {
    if (!navigator.onLine) return;

    try {
      const db = await new Promise((resolve, reject) => {
        const request = window.indexedDB.open("bpaOfflineDB", 3);
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result);
      });

      const transaction = db.transaction("pendingActions", "readwrite");
      const store = transaction.objectStore("pendingActions");

      const pendingActions = await new Promise((resolve, reject) => {
        const request = store.getAll();
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result);
      });

      if (pendingActions && pendingActions.length > 0) {
        for (const action of pendingActions) {
          try {
            switch (action.type) {
              case "addNote":
                await syncAddNote(action.data);
                break;
              case "updateNote":
                await syncUpdateNote(action.data);
                break;
              case "addMember":
                await syncAddMember(action.data);
                break;
              case "updateMember":
                await syncUpdateMember(action.data);
                break;
              case "archiveMember":
                await syncArchiveMember(action.data);
                break;
              case "updateMemberLastSession":
                await syncUpdateMemberLastSession(action.data);
                break;
              case "addMemberGoal":
                await syncAddMemberGoal(action.data);
                break;
              case "updateMemberGoal":
                await syncUpdateMemberGoal(action.data);
                break;
              case "archiveMemberGoal":
                await syncArchiveMemberGoal(action.data);
                break;
              case "addMemberMiniGoal":
                await syncAddMemberMiniGoal(action.data);
                break;
              case "updateMemberMiniGoal":
                await syncUpdateMemberMiniGoal(action.data);
                break;
              case "archiveMemberMiniGoal":
                await syncArchiveMemberMiniGoal(action.data);
                break;
            }

            // Remove the pending action after successful sync
            await new Promise((resolve, reject) => {
              const deleteRequest = store.delete(action.id);
              deleteRequest.onerror = () => reject(deleteRequest.error);
              deleteRequest.onsuccess = () => resolve();
            });
          } catch (error) {
            console.error(`Error syncing action ${action.type}:`, error);
          }
        }
      }
    } catch (error) {
      console.error("Error syncing pending actions:", error);
    }
  };

  // Sync functions for different types of actions
  const syncAddNote = async (noteData) => {
    const { data, error } = await supabase
      .from('notes_bpa_5e7gf')
      .insert([noteData])
      .select();

    if (error) {
      throw error;
    }
    return data[0];
  };

  const syncUpdateNote = async (noteData) => {
    const { id, ...updateData } = noteData;
    const { error } = await supabase
      .from('notes_bpa_5e7gf')
      .update(updateData)
      .eq('id', id);

    if (error) {
      throw error;
    }
  };

  const syncAddMember = async (memberData) => {
    const { data, error } = await supabase
      .from('members_bpa_5e7gf')
      .insert([memberData])
      .select();

    if (error) {
      throw error;
    }
    return data[0];
  };

  const syncUpdateMember = async (memberData) => {
    const { id, ...updateData } = memberData;
    const { error } = await supabase
      .from('members_bpa_5e7gf')
      .update(updateData)
      .eq('id', id);

    if (error) {
      throw error;
    }
  };

  const syncArchiveMember = async (memberId) => {
    const { error } = await supabase
      .from('members_bpa_5e7gf')
      .update({ is_archived: true })
      .eq('id', memberId);

    if (error) {
      throw error;
    }
  };

  const syncUpdateMemberLastSession = async ({ memberId, timestamp }) => {
    const { error } = await supabase
      .from('members_bpa_5e7gf')
      .update({ last_session: timestamp })
      .eq('id', memberId);

    if (error) {
      throw error;
    }
  };

  const syncAddMemberGoal = async (goalData) => {
    const { data, error } = await supabase
      .from('member_goals_bpa_5e7gf')
      .insert([goalData])
      .select();

    if (error) {
      throw error;
    }
    return data[0];
  };

  const syncUpdateMemberGoal = async (goalData) => {
    const { id, ...updateData } = goalData;
    const { error } = await supabase
      .from('member_goals_bpa_5e7gf')
      .update(updateData)
      .eq('id', id);

    if (error) {
      throw error;
    }
  };

  const syncArchiveMemberGoal = async (goalId) => {
    const { error } = await supabase
      .from('member_goals_bpa_5e7gf')
      .update({ is_archived: true })
      .eq('id', goalId);

    if (error) {
      throw error;
    }
  };

  // Mini-Goals sync functions
  const syncAddMemberMiniGoal = async (goalData) => {
    const { data, error } = await supabase
      .from('member_mini_goals_bpa_5e7gf')
      .insert([goalData])
      .select();

    if (error) {
      throw error;
    }
    return data[0];
  };

  const syncUpdateMemberMiniGoal = async (goalData) => {
    const { id, ...updateData } = goalData;
    const { error } = await supabase
      .from('member_mini_goals_bpa_5e7gf')
      .update(updateData)
      .eq('id', id);

    if (error) {
      throw error;
    }
  };

  const syncArchiveMemberMiniGoal = async (goalId) => {
    const { error } = await supabase
      .from('member_mini_goals_bpa_5e7gf')
      .update({ is_archived: true })
      .eq('id', goalId);

    if (error) {
      throw error;
    }
  };

  // Mock data for fallback
  const mockMembers = [
    {
      id: 1,
      name: 'Alex Thompson',
      email: 'alex@example.com',
      date_joined: '2023-01-15',
      country: 'USA',
      state: 'California',
      city: 'Los Angeles',
      phone: '555-1234',
      teams_id: '',
      programs: ['Amazon FBA/FBM'],
      last_session: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      is_archived: false
    },
    {
      id: 2,
      name: 'Maya Patel',
      email: 'maya@example.com',
      date_joined: '2023-02-20',
      country: 'Canada',
      state: 'Ontario',
      city: 'Toronto',
      phone: '555-2345',
      teams_id: '',
      programs: ['Social Media'],
      last_session: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
      is_archived: false
    },
    {
      id: 3,
      name: 'Jordan Smith',
      email: 'jordan@example.com',
      date_joined: '2023-03-10',
      country: 'UK',
      state: 'London',
      city: 'London',
      phone: '555-3456',
      teams_id: '',
      programs: ['Affiliate Marketing'],
      last_session: null,
      is_archived: false
    },
    {
      id: 4,
      name: 'Sam Williams',
      email: 'sam@example.com',
      date_joined: '2023-04-05',
      country: 'Australia',
      state: 'New South Wales',
      city: 'Sydney',
      phone: '555-4567',
      teams_id: '',
      programs: ['SaaS'],
      last_session: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
      is_archived: false
    },
    {
      id: 5,
      name: 'Riley Chen',
      email: 'riley@example.com',
      date_joined: '2023-05-12',
      country: 'USA',
      state: 'New York',
      city: 'New York City',
      phone: '555-5678',
      teams_id: '',
      programs: ['AI'],
      last_session: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
      is_archived: false
    },
    {
      id: 6,
      name: 'Taylor Johnson',
      email: 'taylor@example.com',
      date_joined: '2023-06-18',
      country: 'Germany',
      state: 'Berlin',
      city: 'Berlin',
      phone: '555-6789',
      teams_id: '',
      programs: ['Facebook Groups'],
      last_session: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      is_archived: false
    },
    {
      id: 7,
      name: 'Morgan Lee',
      email: 'morgan@example.com',
      date_joined: '2023-07-22',
      country: 'USA',
      state: 'Texas',
      city: 'Austin',
      phone: '555-7890',
      teams_id: '',
      programs: ['Mindset/Accountability'],
      last_session: null,
      is_archived: false
    },
    {
      id: 8,
      name: 'Casey Brown',
      email: 'casey@example.com',
      date_joined: '2023-08-30',
      country: 'France',
      state: 'Paris',
      city: 'Paris',
      phone: '555-8901',
      teams_id: '',
      programs: ['Strategy'],
      last_session: null,
      is_archived: false
    }
  ];

  const mockNotes = [
    {
      id: 1,
      member_id: 1,
      coach_id: 1,
      coach_name: 'Sarah Johnson',
      title: 'Great progress in Amazon product selection',
      content: 'Alex has shown significant improvement in his Amazon product research process. His product selection criteria are much more refined now, focusing on higher profit margin items.',
      category: 'Performance',
      timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString()
    },
    {
      id: 2,
      member_id: 1,
      coach_id: 2,
      coach_name: 'Mike Rodriguez',
      title: 'Needs work on consistency and mindset',
      content: 'While Alex excels in product research, he needs to focus more on maintaining consistent daily actions and developing a stronger entrepreneurial mindset.',
      category: 'Areas for Improvement',
      timestamp: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString()
    },
    {
      id: 3,
      member_id: 2,
      coach_id: 3,
      coach_name: 'Emily Chen',
      title: 'Excellent social media strategy',
      content: 'Maya has been showing excellent growth in her social media presence, establishing herself as an authority and building a strong community. Her content strategy is well-aligned with her business goals.',
      category: 'Performance',
      timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString()
    },
    {
      id: 4,
      member_id: 4,
      coach_id: 3,
      coach_name: 'Emily Chen',
      title: 'SaaS pricing strategy improvements',
      content: 'Sam has implemented a much more effective tiered pricing strategy for his SaaS product. The new conversion rates show a 15% improvement over the previous pricing model.',
      category: 'Strategy',
      timestamp: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString()
    },
    {
      id: 5,
      member_id: 5,
      coach_id: 3,
      coach_name: 'Emily Chen',
      title: 'AI implementation success',
      content: 'Riley has successfully integrated AI tools into her customer service workflow, resulting in faster response times and higher customer satisfaction ratings.',
      category: 'Performance',
      timestamp: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString()
    },
    {
      id: 6,
      member_id: 6,
      coach_id: 5,
      coach_name: 'Jessica Williams',
      title: 'Facebook Group engagement tactics',
      content: 'Taylor has implemented several new engagement strategies in his Facebook Group that have increased daily active users by 35%. The group is becoming a valuable asset for his business.',
      category: 'Marketing',
      timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString()
    }
  ];

  // Add a new note
  const addNote = async (noteData) => {
    try {
      const newNote = {
        ...noteData,
        timestamp: new Date().toISOString()
      };

      if (navigator.onLine) {
        const { data, error } = await supabase
          .from('notes_bpa_5e7gf')
          .insert([newNote])
          .select();

        if (error) {
          console.error('Error adding note:', error);
          // Fall back to local state update
          const localNote = { id: Date.now(), ...newNote };
          setNotes(prev => [localNote, ...prev]);
          // Save to IndexedDB
          await addNoteToIndexedDB(localNote);
          // Add as pending action
          await addPendingAction("addNote", localNote);
          // Update member's last_session
          updateMemberLastSession(noteData.member_id);
          return localNote;
        }

        // Update notes state
        setNotes(prev => [data[0], ...prev]);
        // Save to IndexedDB
        await addNoteToIndexedDB(data[0]);
        // Update member's last_session
        updateMemberLastSession(noteData.member_id);
        return data[0];
      } else {
        // Offline mode
        const localNote = { id: Date.now(), ...newNote };
        setNotes(prev => [localNote, ...prev]);
        // Save to IndexedDB
        await addNoteToIndexedDB(localNote);
        // Add as pending action
        await addPendingAction("addNote", localNote);
        // Update member's last_session
        updateMemberLastSession(noteData.member_id);
        return localNote;
      }
    } catch (err) {
      console.error('Error in addNote:', err);
      // Fallback to local state update
      const localNote = {
        id: Date.now(),
        ...noteData,
        timestamp: new Date().toISOString()
      };
      setNotes(prev => [localNote, ...prev]);
      // Try to save to IndexedDB
      try {
        await addNoteToIndexedDB(localNote);
        await addPendingAction("addNote", localNote);
      } catch (indexedDBError) {
        console.error('Error saving to IndexedDB:', indexedDBError);
      }
      //Update member's last_session
      updateMemberLastSession(noteData.member_id);
      return localNote;
    }
  };

  // Update an existing note
  const updateNote = async (noteData) => {
    try {
      const { id, ...updateData } = noteData;

      if (navigator.onLine) {
        const { error } = await supabase
          .from('notes_bpa_5e7gf')
          .update(updateData)
          .eq('id', id);

        if (error) {
          console.error('Error updating note:', error);
          // Fall back to local state update
          setNotes(prev => prev.map(note => 
            note.id.toString() === id.toString() ? { ...note, ...updateData } : note
          ));
          // Update in IndexedDB
          await updateNoteInIndexedDB(id, updateData);
          // Add as pending action
          await addPendingAction("updateNote", noteData);
          return { id, ...updateData };
        }

        // Update notes state
        setNotes(prev => prev.map(note => 
          note.id.toString() === id.toString() ? { ...note, ...updateData } : note
        ));
        // Update in IndexedDB
        await updateNoteInIndexedDB(id, updateData);
        return { id, ...updateData };
      } else {
        // Offline mode
        setNotes(prev => prev.map(note => 
          note.id.toString() === id.toString() ? { ...note, ...updateData } : note
        ));
        // Update in IndexedDB
        await updateNoteInIndexedDB(id, updateData);
        // Add as pending action
        await addPendingAction("updateNote", noteData);
        return { id, ...updateData };
      }
    } catch (err) {
      console.error('Error in updateNote:', err);
      // Fallback to local state update
      setNotes(prev => prev.map(note => 
        note.id.toString() === noteData.id.toString() ? { ...note, ...noteData } : note
      ));
      // Try to update in IndexedDB
      try {
        await updateNoteInIndexedDB(noteData.id, noteData);
        await addPendingAction("updateNote", noteData);
      } catch (indexedDBError) {
        console.error('Error updating in IndexedDB:', indexedDBError);
      }
      return noteData;
    }
  };

  // Update note in IndexedDB
  const updateNoteInIndexedDB = async (noteId, updateData) => {
    try {
      const db = await new Promise((resolve, reject) => {
        const request = window.indexedDB.open("bpaOfflineDB", 3);
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result);
      });

      const transaction = db.transaction("notes", "readwrite");
      const store = transaction.objectStore("notes");

      // Get current note data
      const note = await new Promise((resolve, reject) => {
        const getRequest = store.get(noteId);
        getRequest.onerror = () => reject(getRequest.error);
        getRequest.onsuccess = () => resolve(getRequest.result);
      });

      if (note) {
        // Update note data
        const updatedNote = { ...note, ...updateData };
        await new Promise((resolve, reject) => {
          const putRequest = store.put(updatedNote);
          putRequest.onerror = () => reject(putRequest.error);
          putRequest.onsuccess = () => resolve();
        });
      }
    } catch (error) {
      console.error("Error updating note in IndexedDB:", error);
      throw error;
    }
  };

  // Add note to IndexedDB
  const addNoteToIndexedDB = async (note) => {
    try {
      const db = await new Promise((resolve, reject) => {
        const request = window.indexedDB.open("bpaOfflineDB", 3);
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result);
      });

      const transaction = db.transaction("notes", "readwrite");
      const store = transaction.objectStore("notes");

      await new Promise((resolve, reject) => {
        const addRequest = store.put(note);
        addRequest.onerror = () => reject(addRequest.error);
        addRequest.onsuccess = () => resolve();
      });
    } catch (error) {
      console.error("Error adding note to IndexedDB:", error);
      throw error;
    }
  };

  // Update member's last session date
  const updateMemberLastSession = async (memberId) => {
    try {
      const now = new Date().toISOString();

      if (navigator.onLine) {
        const { error } = await supabase
          .from('members_bpa_5e7gf')
          .update({ last_session: now })
          .eq('id', memberId);

        if (error) {
          console.error('Error updating member last_session:', error);
          // Fallback to local state update
          setMembers(prev => prev.map(member => 
            member.id === memberId ? { ...member, last_session: now } : member
          ));
          // Update in IndexedDB
          await updateMemberInIndexedDB(memberId, { last_session: now });
          // Add as pending action
          await addPendingAction("updateMemberLastSession", { memberId, timestamp: now });
        } else {
          // Update local state
          setMembers(prev => prev.map(member => 
            member.id === memberId ? { ...member, last_session: now } : member
          ));
          // Update in IndexedDB
          await updateMemberInIndexedDB(memberId, { last_session: now });
        }
      } else {
        // Offline mode
        setMembers(prev => prev.map(member => 
          member.id === memberId ? { ...member, last_session: now } : member
        ));
        // Update in IndexedDB
        await updateMemberInIndexedDB(memberId, { last_session: now });
        // Add as pending action
        await addPendingAction("updateMemberLastSession", { memberId, timestamp: now });
      }
    } catch (err) {
      console.error('Error in updateMemberLastSession:', err);
      // Fallback to local state update
      setMembers(prev => prev.map(member => 
        member.id === memberId ? { ...member, last_session: new Date().toISOString() } : member
      ));
      // Try to update in IndexedDB
      try {
        const now = new Date().toISOString();
        await updateMemberInIndexedDB(memberId, { last_session: now });
        await addPendingAction("updateMemberLastSession", { memberId, timestamp: now });
      } catch (indexedDBError) {
        console.error('Error updating in IndexedDB:', indexedDBError);
      }
    }
  };

  // Update member in IndexedDB
  const updateMemberInIndexedDB = async (memberId, updateData) => {
    try {
      const db = await new Promise((resolve, reject) => {
        const request = window.indexedDB.open("bpaOfflineDB", 3);
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result);
      });

      const transaction = db.transaction("members", "readwrite");
      const store = transaction.objectStore("members");

      // Get current member data
      const member = await new Promise((resolve, reject) => {
        const getRequest = store.get(memberId);
        getRequest.onerror = () => reject(getRequest.error);
        getRequest.onsuccess = () => resolve(getRequest.result);
      });

      if (member) {
        // Update member data
        const updatedMember = { ...member, ...updateData };
        await new Promise((resolve, reject) => {
          const putRequest = store.put(updatedMember);
          putRequest.onerror = () => reject(putRequest.error);
          putRequest.onsuccess = () => resolve();
        });
      }
    } catch (error) {
      console.error("Error updating member in IndexedDB:", error);
      throw error;
    }
  };

  // Add a new member
  const addMember = async (memberData) => {
    try {
      const newMemberData = {
        ...memberData,
        is_archived: false
      };

      if (navigator.onLine) {
        console.log('Adding member online:', newMemberData);
        // Make sure there's no id property in the data to prevent insertion errors
        const { id, ...dataToInsert } = newMemberData;

        const { data, error } = await supabase
          .from('members_bpa_5e7gf')
          .insert([dataToInsert])
          .select();

        if (error) {
          console.error('Error adding member:', error);
          throw new Error(error.message);
        }

        console.log('Member added successfully:', data[0]);
        // Update members state
        setMembers(prev => [...prev, data[0]]);
        // Save to IndexedDB
        await addMemberToIndexedDB(data[0]);
        return data[0];
      } else {
        // Offline mode - generate temporary ID
        const tempMember = { ...newMemberData, id: `temp_${Date.now()}` };
        // Update local state
        setMembers(prev => [...prev, tempMember]);
        // Save to IndexedDB
        await addMemberToIndexedDB(tempMember);
        // Add as pending action
        await addPendingAction("addMember", newMemberData);
        return tempMember;
      }
    } catch (err) {
      console.error('Error in addMember:', err);
      throw err;
    }
  };

  // Update an existing member
  const updateMember = async (memberData) => {
    try {
      const { id, ...updateData } = memberData;

      if (navigator.onLine) {
        const { error } = await supabase
          .from('members_bpa_5e7gf')
          .update(updateData)
          .eq('id', id);

        if (error) {
          console.error('Error updating member:', error);
          throw new Error(error.message);
        }

        // Update members state
        setMembers(prev => prev.map(member => 
          member.id.toString() === id.toString() ? { ...member, ...updateData } : member
        ));
        // Update in IndexedDB
        await updateMemberInIndexedDB(id, updateData);
        return { id, ...updateData };
      } else {
        // Offline mode
        // Update local state
        setMembers(prev => prev.map(member => 
          member.id.toString() === id.toString() ? { ...member, ...updateData } : member
        ));
        // Update in IndexedDB
        await updateMemberInIndexedDB(id, updateData);
        // Add as pending action
        await addPendingAction("updateMember", memberData);
        return { id, ...updateData };
      }
    } catch (err) {
      console.error('Error in updateMember:', err);
      throw err;
    }
  };

  // Archive a member (instead of deleting)
  const archiveMember = async (memberId) => {
    try {
      if (navigator.onLine) {
        const { error } = await supabase
          .from('members_bpa_5e7gf')
          .update({ is_archived: true })
          .eq('id', memberId);

        if (error) {
          console.error('Error archiving member:', error);
          throw new Error(error.message);
        }

        // Update members state
        setMembers(prev => prev.map(member => 
          member.id.toString() === memberId.toString() ? { ...member, is_archived: true } : member
        ));
        // Update IndexedDB
        await updateMemberInIndexedDB(memberId, { is_archived: true });
        return true;
      } else {
        // Offline mode
        // Update local state
        setMembers(prev => prev.map(member => 
          member.id.toString() === memberId.toString() ? { ...member, is_archived: true } : member
        ));
        // Update IndexedDB
        await updateMemberInIndexedDB(memberId, { is_archived: true });
        // Add as pending action
        await addPendingAction("archiveMember", memberId);
        return true;
      }
    } catch (err) {
      console.error('Error in archiveMember:', err);
      throw err;
    }
  };

  // Restore an archived member
  const restoreMember = async (memberId) => {
    try {
      if (navigator.onLine) {
        const { error } = await supabase
          .from('members_bpa_5e7gf')
          .update({ is_archived: false })
          .eq('id', memberId);

        if (error) {
          console.error('Error restoring member:', error);
          throw new Error(error.message);
        }

        // Update members state
        setMembers(prev => prev.map(member => 
          member.id.toString() === memberId.toString() ? { ...member, is_archived: false } : member
        ));
        // Update IndexedDB
        await updateMemberInIndexedDB(memberId, { is_archived: false });
        return true;
      } else {
        // Offline mode
        // Update local state
        setMembers(prev => prev.map(member => 
          member.id.toString() === memberId.toString() ? { ...member, is_archived: false } : member
        ));
        // Update IndexedDB
        await updateMemberInIndexedDB(memberId, { is_archived: false });
        // Add as pending action
        await addPendingAction("updateMember", { id: memberId, is_archived: false });
        return true;
      }
    } catch (err) {
      console.error('Error in restoreMember:', err);
      throw err;
    }
  };

  // Add member to IndexedDB
  const addMemberToIndexedDB = async (member) => {
    try {
      const db = await new Promise((resolve, reject) => {
        const request = window.indexedDB.open("bpaOfflineDB", 3);
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result);
      });

      const transaction = db.transaction("members", "readwrite");
      const store = transaction.objectStore("members");

      await new Promise((resolve, reject) => {
        const addRequest = store.put(member);
        addRequest.onerror = () => reject(addRequest.error);
        addRequest.onsuccess = () => resolve();
      });
    } catch (error) {
      console.error("Error adding member to IndexedDB:", error);
      throw error;
    }
  };

  // Member Goals Functions
  const getMemberGoals = (memberId) => {
    if (!memberId) return [];
    return memberGoals.filter(goal => String(goal.member_id) === String(memberId));
  };

  const addMemberGoal = async (goalData) => {
    try {
      const newGoal = {
        ...goalData,
        created_at: new Date().toISOString(),
        is_archived: false
      };

      if (navigator.onLine) {
        const { data, error } = await supabase
          .from('member_goals_bpa_5e7gf')
          .insert([newGoal])
          .select();

        if (error) {
          console.error('Error adding member goal:', error);
          // Fall back to local state update
          const localGoal = { id: Date.now(), ...newGoal };
          setMemberGoals(prev => [localGoal, ...prev]);
          // Save to IndexedDB
          await addGoalToIndexedDB(localGoal);
          // Add as pending action
          await addPendingAction("addMemberGoal", localGoal);
          return localGoal;
        }

        // Update goals state
        setMemberGoals(prev => [data[0], ...prev]);
        // Save to IndexedDB
        await addGoalToIndexedDB(data[0]);
        return data[0];
      } else {
        // Offline mode
        const localGoal = { id: Date.now(), ...newGoal };
        setMemberGoals(prev => [localGoal, ...prev]);
        // Save to IndexedDB
        await addGoalToIndexedDB(localGoal);
        // Add as pending action
        await addPendingAction("addMemberGoal", localGoal);
        return localGoal;
      }
    } catch (err) {
      console.error('Error in addMemberGoal:', err);
      // Fallback to local state update
      const localGoal = {
        id: Date.now(),
        ...goalData,
        created_at: new Date().toISOString(),
        is_archived: false
      };
      setMemberGoals(prev => [localGoal, ...prev]);
      // Try to save to IndexedDB
      try {
        await addGoalToIndexedDB(localGoal);
        await addPendingAction("addMemberGoal", localGoal);
      } catch (indexedDBError) {
        console.error('Error saving goal to IndexedDB:', indexedDBError);
      }
      return localGoal;
    }
  };

  const updateMemberGoal = async (goalData) => {
    try {
      const { id, ...updateData } = goalData;

      if (navigator.onLine) {
        const { error } = await supabase
          .from('member_goals_bpa_5e7gf')
          .update(updateData)
          .eq('id', id);

        if (error) {
          console.error('Error updating member goal:', error);
          // Fall back to local state update
          setMemberGoals(prev => prev.map(goal => 
            goal.id.toString() === id.toString() ? { ...goal, ...updateData } : goal
          ));
          // Update in IndexedDB
          await updateGoalInIndexedDB(id, updateData);
          // Add as pending action
          await addPendingAction("updateMemberGoal", goalData);
          return { id, ...updateData };
        }

        // Update goals state
        setMemberGoals(prev => prev.map(goal => 
          goal.id.toString() === id.toString() ? { ...goal, ...updateData } : goal
        ));
        // Update in IndexedDB
        await updateGoalInIndexedDB(id, updateData);
        return { id, ...updateData };
      } else {
        // Offline mode
        setMemberGoals(prev => prev.map(goal => 
          goal.id.toString() === id.toString() ? { ...goal, ...updateData } : goal
        ));
        // Update in IndexedDB
        await updateGoalInIndexedDB(id, updateData);
        // Add as pending action
        await addPendingAction("updateMemberGoal", goalData);
        return { id, ...updateData };
      }
    } catch (err) {
      console.error('Error in updateMemberGoal:', err);
      // Fallback to local state update
      setMemberGoals(prev => prev.map(goal => 
        goal.id.toString() === goalData.id.toString() ? { ...goal, ...goalData } : goal
      ));
      // Try to update in IndexedDB
      try {
        await updateGoalInIndexedDB(goalData.id, goalData);
        await addPendingAction("updateMemberGoal", goalData);
      } catch (indexedDBError) {
        console.error('Error updating goal in IndexedDB:', indexedDBError);
      }
      return goalData;
    }
  };

  const archiveMemberGoal = async (goalId) => {
    try {
      if (navigator.onLine) {
        const { error } = await supabase
          .from('member_goals_bpa_5e7gf')
          .update({ is_archived: true })
          .eq('id', goalId);

        if (error) {
          console.error('Error archiving member goal:', error);
          throw new Error(error.message);
        }

        // Update goals state
        setMemberGoals(prev => prev.map(goal => 
          goal.id.toString() === goalId.toString() ? { ...goal, is_archived: true } : goal
        ));
        // Update IndexedDB
        await updateGoalInIndexedDB(goalId, { is_archived: true });
        return true;
      } else {
        // Offline mode
        // Update local state
        setMemberGoals(prev => prev.map(goal => 
          goal.id.toString() === goalId.toString() ? { ...goal, is_archived: true } : goal
        ));
        // Update IndexedDB
        await updateGoalInIndexedDB(goalId, { is_archived: true });
        // Add as pending action
        await addPendingAction("archiveMemberGoal", goalId);
        return true;
      }
    } catch (err) {
      console.error('Error in archiveMemberGoal:', err);
      throw err;
    }
  };

  const restoreMemberGoal = async (goalId) => {
    try {
      if (navigator.onLine) {
        const { error } = await supabase
          .from('member_goals_bpa_5e7gf')
          .update({ is_archived: false })
          .eq('id', goalId);

        if (error) {
          console.error('Error restoring member goal:', error);
          throw new Error(error.message);
        }

        // Update goals state
        setMemberGoals(prev => prev.map(goal => 
          goal.id.toString() === goalId.toString() ? { ...goal, is_archived: false } : goal
        ));
        // Update IndexedDB
        await updateGoalInIndexedDB(goalId, { is_archived: false });
        return true;
      } else {
        // Offline mode
        // Update local state
        setMemberGoals(prev => prev.map(goal => 
          goal.id.toString() === goalId.toString() ? { ...goal, is_archived: false } : goal
        ));
        // Update IndexedDB
        await updateGoalInIndexedDB(goalId, { is_archived: false });
        // Add as pending action
        await addPendingAction("updateMemberGoal", { id: goalId, is_archived: false });
        return true;
      }
    } catch (err) {
      console.error('Error in restoreMemberGoal:', err);
      throw err;
    }
  };

  // Member Mini-Goals Functions
  const getMemberMiniGoals = (memberId) => {
    if (!memberId) return [];
    return memberMiniGoals.filter(goal => String(goal.member_id) === String(memberId));
  };

  const addMemberMiniGoal = async (goalData) => {
    try {
      const newGoal = {
        ...goalData,
        created_at: new Date().toISOString(),
        is_archived: false
      };

      if (navigator.onLine) {
        const { data, error } = await supabase
          .from('member_mini_goals_bpa_5e7gf')
          .insert([newGoal])
          .select();

        if (error) {
          console.error('Error adding member mini-goal:', error);
          // Fall back to local state update
          const localGoal = { id: Date.now(), ...newGoal };
          setMemberMiniGoals(prev => [localGoal, ...prev]);
          // Save to IndexedDB
          await addMiniGoalToIndexedDB(localGoal);
          // Add as pending action
          await addPendingAction("addMemberMiniGoal", localGoal);
          return localGoal;
        }

        // Update goals state
        setMemberMiniGoals(prev => [data[0], ...prev]);
        // Save to IndexedDB
        await addMiniGoalToIndexedDB(data[0]);
        return data[0];
      } else {
        // Offline mode
        const localGoal = { id: Date.now(), ...newGoal };
        setMemberMiniGoals(prev => [localGoal, ...prev]);
        // Save to IndexedDB
        await addMiniGoalToIndexedDB(localGoal);
        // Add as pending action
        await addPendingAction("addMemberMiniGoal", localGoal);
        return localGoal;
      }
    } catch (err) {
      console.error('Error in addMemberMiniGoal:', err);
      // Fallback to local state update
      const localGoal = {
        id: Date.now(),
        ...goalData,
        created_at: new Date().toISOString(),
        is_archived: false
      };
      setMemberMiniGoals(prev => [localGoal, ...prev]);
      // Try to save to IndexedDB
      try {
        await addMiniGoalToIndexedDB(localGoal);
        await addPendingAction("addMemberMiniGoal", localGoal);
      } catch (indexedDBError) {
        console.error('Error saving mini-goal to IndexedDB:', indexedDBError);
      }
      return localGoal;
    }
  };

  const updateMemberMiniGoal = async (goalData) => {
    try {
      const { id, ...updateData } = goalData;

      if (navigator.onLine) {
        const { error } = await supabase
          .from('member_mini_goals_bpa_5e7gf')
          .update(updateData)
          .eq('id', id);

        if (error) {
          console.error('Error updating member mini-goal:', error);
          // Fall back to local state update
          setMemberMiniGoals(prev => prev.map(goal => 
            goal.id.toString() === id.toString() ? { ...goal, ...updateData } : goal
          ));
          // Update in IndexedDB
          await updateMiniGoalInIndexedDB(id, updateData);
          // Add as pending action
          await addPendingAction("updateMemberMiniGoal", goalData);
          return { id, ...updateData };
        }

        // Update goals state
        setMemberMiniGoals(prev => prev.map(goal => 
          goal.id.toString() === id.toString() ? { ...goal, ...updateData } : goal
        ));
        // Update in IndexedDB
        await updateMiniGoalInIndexedDB(id, updateData);
        return { id, ...updateData };
      } else {
        // Offline mode
        setMemberMiniGoals(prev => prev.map(goal => 
          goal.id.toString() === id.toString() ? { ...goal, ...updateData } : goal
        ));
        // Update in IndexedDB
        await updateMiniGoalInIndexedDB(id, updateData);
        // Add as pending action
        await addPendingAction("updateMemberMiniGoal", goalData);
        return { id, ...updateData };
      }
    } catch (err) {
      console.error('Error in updateMemberMiniGoal:', err);
      // Fallback to local state update
      setMemberMiniGoals(prev => prev.map(goal => 
        goal.id.toString() === goalData.id.toString() ? { ...goal, ...goalData } : goal
      ));
      // Try to update in IndexedDB
      try {
        await updateMiniGoalInIndexedDB(goalData.id, goalData);
        await addPendingAction("updateMemberMiniGoal", goalData);
      } catch (indexedDBError) {
        console.error('Error updating mini-goal in IndexedDB:', indexedDBError);
      }
      return goalData;
    }
  };

  const archiveMemberMiniGoal = async (goalId) => {
    try {
      if (navigator.onLine) {
        const { error } = await supabase
          .from('member_mini_goals_bpa_5e7gf')
          .update({ is_archived: true })
          .eq('id', goalId);

        if (error) {
          console.error('Error archiving member mini-goal:', error);
          throw new Error(error.message);
        }

        // Update goals state
        setMemberMiniGoals(prev => prev.map(goal => 
          goal.id.toString() === goalId.toString() ? { ...goal, is_archived: true } : goal
        ));
        // Update IndexedDB
        await updateMiniGoalInIndexedDB(goalId, { is_archived: true });
        return true;
      } else {
        // Offline mode
        // Update local state
        setMemberMiniGoals(prev => prev.map(goal => 
          goal.id.toString() === goalId.toString() ? { ...goal, is_archived: true } : goal
        ));
        // Update IndexedDB
        await updateMiniGoalInIndexedDB(goalId, { is_archived: true });
        // Add as pending action
        await addPendingAction("archiveMemberMiniGoal", goalId);
        return true;
      }
    } catch (err) {
      console.error('Error in archiveMemberMiniGoal:', err);
      throw err;
    }
  };

  const restoreMemberMiniGoal = async (goalId) => {
    try {
      if (navigator.onLine) {
        const { error } = await supabase
          .from('member_mini_goals_bpa_5e7gf')
          .update({ is_archived: false })
          .eq('id', goalId);

        if (error) {
          console.error('Error restoring member mini-goal:', error);
          throw new Error(error.message);
        }

        // Update goals state
        setMemberMiniGoals(prev => prev.map(goal => 
          goal.id.toString() === goalId.toString() ? { ...goal, is_archived: false } : goal
        ));
        // Update IndexedDB
        await updateMiniGoalInIndexedDB(goalId, { is_archived: false });
        return true;
      } else {
        // Offline mode
        // Update local state
        setMemberMiniGoals(prev => prev.map(goal => 
          goal.id.toString() === goalId.toString() ? { ...goal, is_archived: false } : goal
        ));
        // Update IndexedDB
        await updateMiniGoalInIndexedDB(goalId, { is_archived: false });
        // Add as pending action
        await addPendingAction("updateMemberMiniGoal", { id: goalId, is_archived: false });
        return true;
      }
    } catch (err) {
      console.error('Error in restoreMemberMiniGoal:', err);
      throw err;
    }
  };

  // Goal IndexedDB operations
  const addGoalToIndexedDB = async (goal) => {
    try {
      const db = await new Promise((resolve, reject) => {
        const request = window.indexedDB.open("bpaOfflineDB", 3);
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result);
      });

      const transaction = db.transaction("memberGoals", "readwrite");
      const store = transaction.objectStore("memberGoals");

      await new Promise((resolve, reject) => {
        const addRequest = store.put(goal);
        addRequest.onerror = () => reject(addRequest.error);
        addRequest.onsuccess = () => resolve();
      });
    } catch (error) {
      console.error("Error adding goal to IndexedDB:", error);
      throw error;
    }
  };

  const updateGoalInIndexedDB = async (goalId, updateData) => {
    try {
      const db = await new Promise((resolve, reject) => {
        const request = window.indexedDB.open("bpaOfflineDB", 3);
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result);
      });

      const transaction = db.transaction("memberGoals", "readwrite");
      const store = transaction.objectStore("memberGoals");

      // Get current goal data
      const goal = await new Promise((resolve, reject) => {
        const getRequest = store.get(goalId);
        getRequest.onerror = () => reject(getRequest.error);
        getRequest.onsuccess = () => resolve(getRequest.result);
      });

      if (goal) {
        // Update goal data
        const updatedGoal = { ...goal, ...updateData };
        await new Promise((resolve, reject) => {
          const putRequest = store.put(updatedGoal);
          putRequest.onerror = () => reject(putRequest.error);
          putRequest.onsuccess = () => resolve();
        });
      }
    } catch (error) {
      console.error("Error updating goal in IndexedDB:", error);
      throw error;
    }
  };

  // Mini-Goal IndexedDB operations
  const addMiniGoalToIndexedDB = async (goal) => {
    try {
      const db = await new Promise((resolve, reject) => {
        const request = window.indexedDB.open("bpaOfflineDB", 3);
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result);
      });

      const transaction = db.transaction("memberMiniGoals", "readwrite");
      const store = transaction.objectStore("memberMiniGoals");

      await new Promise((resolve, reject) => {
        const addRequest = store.put(goal);
        addRequest.onerror = () => reject(addRequest.error);
        addRequest.onsuccess = () => resolve();
      });
    } catch (error) {
      console.error("Error adding mini-goal to IndexedDB:", error);
      throw error;
    }
  };

  const updateMiniGoalInIndexedDB = async (goalId, updateData) => {
    try {
      const db = await new Promise((resolve, reject) => {
        const request = window.indexedDB.open("bpaOfflineDB", 3);
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result);
      });

      const transaction = db.transaction("memberMiniGoals", "readwrite");
      const store = transaction.objectStore("memberMiniGoals");

      // Get current goal data
      const goal = await new Promise((resolve, reject) => {
        const getRequest = store.get(goalId);
        getRequest.onerror = () => reject(getRequest.error);
        getRequest.onsuccess = () => resolve(getRequest.result);
      });

      if (goal) {
        // Update goal data
        const updatedGoal = { ...goal, ...updateData };
        await new Promise((resolve, reject) => {
          const putRequest = store.put(updatedGoal);
          putRequest.onerror = () => reject(putRequest.error);
          putRequest.onsuccess = () => resolve();
        });
      }
    } catch (error) {
      console.error("Error updating mini-goal in IndexedDB:", error);
      throw error;
    }
  };

  // Get notes for a specific member
  const getMemberNotes = (memberId) => {
    if (!memberId) return [];
    return notes.filter(note => String(note.member_id) === String(memberId));
  };

  // Get recent notes
  const getRecentNotes = (limit = 10) => {
    return notes
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      .slice(0, limit);
  };

  // Search members by name or email
  const searchMembers = (query) => {
    if (!query) return [];
    query = query.toLowerCase();
    return members.filter(member => 
      member.name.toLowerCase().includes(query) ||
      member.email.toLowerCase().includes(query) ||
      (member.city && member.city.toLowerCase().includes(query))
    );
  };

  // Toggle archived members visibility
  const toggleShowArchived = () => {
    setShowArchived(prev => !prev);
  };

  // Get active and/or archived members based on current filter
  const getFilteredMembers = () => {
    return showArchived ? members : members.filter(member => !member.is_archived);
  };

  const value = {
    members: getFilteredMembers(),
    allMembers: members,
    notes,
    memberGoals,
    memberMiniGoals,
    loading,
    offlineMode,
    showArchived,
    toggleShowArchived,
    addNote,
    updateNote,
    addMember,
    updateMember,
    archiveMember, // Changed from deleteMember to archiveMember
    restoreMember,
    getMemberNotes,
    getRecentNotes,
    searchMembers,
    getMemberGoals,
    addMemberGoal,
    updateMemberGoal,
    archiveMemberGoal,
    restoreMemberGoal,
    getMemberMiniGoals,
    addMemberMiniGoal,
    updateMemberMiniGoal,
    archiveMemberMiniGoal,
    restoreMemberMiniGoal
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};
